import { ListingProduct } from '@rdss/client'
import { store } from '~/store'
import { DetailProduct } from '~/types'

declare const jQuery: { post }
declare const _satellite: { track }

/**
 * Checks if product is sponsored.
 */
export function isSponsoredProduct(product: ListingProduct | DetailProduct): boolean {
    return product?.sponsored === true
}

/**
 * Checks if page was called from the webshop.
 */
export function wasCalledFromShop(): boolean {
    return !!document.referrer.match('^https://(www.)?zooroyal.(de|at|cz)/') ||
        !!document.referrer.match('^https://(.*).rdss.it/')
}

/**
 * Checks if page was called from the webshop.
 */
export async function loadBanner(categoryId: number, searchTerm: string) {
    try {
        let json
        const response = await fetch(`https://dev.zooroyal.rdss.it/frontend/citrusadsbanner/data?category=${categoryId}&search=${searchTerm}`)
        if (response.ok) {
            json = await response.json()
        }
        return json
    } catch (error) {
        console.error('Error while getting citrusad banner', error)
        return null
    }
}

/**
 * Tracks a citrus ad event.
 */
export async function trackCitrusAd(event: string, product: ListingProduct | DetailProduct): Promise<void> {
    try {
        if (['click', 'impression'].includes(event)) {
            await jQuery.post(`/frontend/citrusads/${event}`, { citrusAdId: product.citrusAdId })
        } else {
            console.warn('Trying to track unknown citrusAd event', event)
        }
    } catch (error) {
        console.error('An error occurred tracking citrusAd event', event, 'for citrusAdId', product.citrusAdId, error)
    }
}

/**
 * Appends tracking information to the given URL.
 */
export function appendQuicklinkTrackingInfo(url: string, label: string): string {
    try {
        const trackingUrl = new URL(url, location.href)
        trackingUrl.searchParams.append('quicklink', encodeURIComponent(label))
        return trackingUrl.href
    } catch {
        return url
    }
}

/**
 * Compares tracking data.
 */
export function hasTrackingDataChanged(oldData: object, newData: object): boolean {
    try {
        return JSON.stringify(oldData) !== JSON.stringify(newData)
    } catch {
        return true
    }
}

/**
 * Create Tracking Products.
 */
export function trackingProducts(products: ListingProduct[], filterSponsored = false, category: string | null = null) {
    const trackingProducts = products
        .filter(item => !!item.sku)
        .map((item, index) => {
            item.position = index + 1
            return item
        })
        .reduce((acc, item) => {
            if (!filterSponsored || !isSponsoredProduct(item)) {
                acc[item.sku.toString()] = JSON.parse(JSON.stringify(item))
            }
            return acc
        }, {})
    const trackingCategory = category ? category : window.pageData.page.name.replace(/^zooroyal:/, '')
        .replace(/:/g, '_') || ''
    for (const key in trackingProducts) {
        trackingProducts[key] = {
            ...trackingProducts[key],
            availability: 'sofort versandfertig',
            category: trackingCategory,
            context: 'product-list',
            currency: trackingProducts[key].price?.currency || 'EUR',
            detailLink: trackingProducts[key].seoUrl,
            ean: 'n/a',
            id: trackingProducts[key].sku,
            position: trackingProducts[key].position.toString(),
            price: trackingProducts[key].price?.gross?.toString(),
            priceNet: trackingProducts[key].price?.net?.toString(),
            rrp: trackingProducts[key].price?.grossPseudo?.toString(),
            savingsAmount: (
                (trackingProducts[key].price?.grossPseudo || 0) - (trackingProducts[key].price?.gross || 0)
            ).toString(),
            savingsPercent: trackingProducts[key].price?.pseudoPercent?.toString(),
            units: '1',
            variant:
                (trackingProducts[key].price?.purchaseQuantity?.toLocaleString('de') || '') +
                (trackingProducts[key].price?.unit || ''),
            sponsored: trackingProducts[key].sponsored ? 'true' : 'false',
        }
        ;['badges', 'rating', '_links'].forEach(node => delete trackingProducts[key][node])
    }
    return trackingProducts
}

/**
 * Add Adobe tracking source parameters.
 */
export function adobeTrackingSource(params: {
    searchUrl: string
    searchTerm : string
    searchType: string
    category: string }): string {
    // if cz and at shop then return url
    const adobeSearchParams = `${params.searchTerm}-${params.searchType}-${params.category}`
    const urlPath = new URL(params.searchUrl, location.href)
    urlPath.searchParams.append('source', adobeSearchParams)
    return urlPath.href
}

/**
 * Base Adobe Event Tracking.
 */
export function trackAdobeEvent(event: string, data: object): void {
    ['emarsys', 'peerius', 'trustedshops'].forEach(node => delete data[node])
    updatePageData(data)
    if (typeof(_satellite)!=='undefined') {
        _satellite?.track(event, data)
    }
}

/**
 * Update pageData.
 */
export function updatePageData(data: object): void {
    window.pageData = { ...window.pageData, ...data }
}

/**
 * Adobe Tracking Context.
 */
export function adobeTrackingContext(context: string | null = null): { trackingCategory: string, trackingContext: string } {
    let trackingCategory = null
    let trackingContext = context
    if (location.pathname === '/') {
        trackingCategory = 'homepage'
        trackingContext = trackingContext.replace('-category-', '-homepage-')
    } else if (location.pathname.startsWith('/account')) {
        trackingCategory = 'account'
        trackingContext = trackingContext.replace('-category-', '-account-')
    } else if (location.pathname === '/search') {
        trackingCategory = 'search'
        trackingContext = trackingContext.replace('-category-', '-search-')
    }
    if (!trackingCategory && window.pageData?.page?.name) {
        trackingCategory = window.pageData?.page.name
            .replace(/^zooroyal:/, '')
            .replace(/:/g, '_') || ''
    }
    return {
        trackingCategory: trackingCategory || 'unbekannt',
        trackingContext: trackingContext || 'unbekannt',
    }
}

/**
 * Create add to cart tracking data.
 */
export function addToCartTrackingData(product: ListingProduct,
    detail: { orderNumber?: string, quantity: number, context: string }) {
    const { trackingCategory, trackingContext } = adobeTrackingContext(detail.context)
    const cartItem = store.state.cart?.items?.find((item) => { return item.sku === detail.orderNumber })
    const quantity = detail.quantity - (cartItem?.quantity || 0)
    if (typeof(_satellite)!=='undefined' && quantity > 0) {
        _satellite?.track('addToCart', {
            addToCart: {
                context: trackingContext,
                product: {
                    [detail.orderNumber]: {
                        id: product.sku,
                        sponsored: isSponsoredProduct(product).toString(),
                        category: trackingCategory,
                        brand: product.brand,
                        name: product.name,
                        price: product.price?.gross?.toString(),
                        position: product.position?.toString() || '1',
                        totalProductValue: product.price?.grossPseudo?.toString(),
                        units: quantity.toString(),
                    },
                },
            },
        })
        if (product.citrusAdId) {
            trackCitrusAd('click', product)
        }
    }
}
